import React, { memo, useCallback, useDeferredValue, useEffect, useMemo, useState } from "react";
import {
	Box,
	Button,
	Chip,
	InputAdornment,
	Paper,
	Stack,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TablePagination,
	TableRow,
	TableSortLabel,
	TextField,
	Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { GlobalState } from "context/GlobalContext";
import { AuthState } from "context/AuthContext";
import { pythonVmInstance } from "utils/AxiosInstances/RareboxPythonInstance";
import { canDownloadRefundTracker, refundTrackerRoleAccess } from "./data";

const formatCurrency = (value) => `₹ ${Number(value || 0).toLocaleString("en-IN")}`;

const toNumber = (value) => {
	const parsed = Number(String(value ?? "").replace(/[^0-9.-]/g, ""));
	return Number.isFinite(parsed) ? parsed : 0;
};

const toCsvValue = (value) => {
	if (value === null || value === undefined) return '""';
	const stringValue = typeof value === "object" ? JSON.stringify(value) : String(value);
	return `"${stringValue.replace(/"/g, '""')}"`;
};

const formatSingleDate = (value) => {
	if (value === null || value === undefined || value === "") return "";
	let text = String(value).trim();
	if (!text || text === "-") return "";

	text = text.replace(/\s+/g, " ").replace(/ UTC$/i, "Z");

	// Snapmint / DD/MM/YYYY (or D/M/YYYY)
	const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (dmy) {
		const day = dmy[1].padStart(2, "0");
		const month = dmy[2].padStart(2, "0");
		const year = dmy[3];
		const parsed = dayjs(`${year}-${month}-${day}`);
		return parsed.isValid() ? parsed.format("DD MMM YYYY") : text;
	}

	// ISO / BigQuery timestamps: use calendar date part to avoid timezone day-shift
	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const parsed = dayjs(`${iso[1]}-${iso[2]}-${iso[3]}`);
		return parsed.isValid() ? parsed.format("DD MMM YYYY") : text;
	}

	// Fallback
	let parsed = dayjs(text);
	if (!parsed.isValid()) {
		parsed = dayjs(text.replace(/[+-]\d{2}:?\d{0,2}$/, "").trim());
	}
	return parsed.isValid() ? parsed.format("DD MMM YYYY") : text;
};

const formatSingleCsvDate = (value) => {
	if (value === null || value === undefined || value === "") return "";
	if (typeof value === "object") {
		value = value.value ?? value.date ?? value.datetime ?? JSON.stringify(value);
	}

	let text = String(value).trim();
	if (!text || text === "-" || text.toLowerCase() === "null" || text.toLowerCase() === "none") return "";

	text = text.replace(/\s+/g, " ").replace(/ UTC$/i, "Z");

	const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
	if (dmy) {
		const parsed = dayjs(`${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`);
		return parsed.isValid() ? parsed.format("DD/MM/YYYY") : text;
	}

	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const parsed = dayjs(`${iso[1]}-${iso[2]}-${iso[3]}`);
		return parsed.isValid() ? parsed.format("DD/MM/YYYY") : text;
	}

	let parsed = dayjs(text);
	if (!parsed.isValid()) {
		parsed = dayjs(text.replace(/[+-]\d{2}:?\d{0,2}$/, "").trim());
	}
	return parsed.isValid() ? parsed.format("DD/MM/YYYY") : text;
};

const formatDateValue = (value) => {
	if (value === null || value === undefined || value === "") return "-";

	// Keep comma-separated refund dates intact (e.g. "03/07/2026, 12/07/2026")
	const parts = String(value)
		.split(",")
		.map((part) => formatSingleDate(part.trim()))
		.filter(Boolean);

	return parts.length ? [...new Set(parts)].join(", ") : "-";
};

const formatCsvDateValue = (value) => {
	if (value === null || value === undefined || value === "") return "";

	const raw =
		typeof value === "object" ? value.value ?? value.date ?? value.datetime ?? "" : value;

	const parts = String(raw)
		.split(",")
		.map((part) => formatSingleCsvDate(part.trim()))
		.filter(Boolean);

	return parts.length ? [...new Set(parts)].join(", ") : "";
};

const splitMultiValues = (value) => {
	if (value === null || value === undefined || value === "") return [];
	return String(value)
		.split(",")
		.map((part) => part.trim())
		.filter((part) => part && part !== "-" && part.toLowerCase() !== "null" && part.toLowerCase() !== "none");
};

const toSafeText = (value) => {
	if (value === null || value === undefined || value === "") return "-";
	return String(value);
};

const isDateColumnKey = (key) =>
	/date|_at$/i.test(String(key || "")) || ["processed_at", "created_at", "refund_at"].includes(key);

const isMultiTextColumnKey = (key) =>
	["refund_id", "payment_id", "pay_id", "bank_arn", "bank_rrn", "partner_refund_id"].includes(key);

const getExportCellValue = (row, col) => {
	const value = row?.[col.key];

	if (col.type === "currency") {
		return toNumber(value);
	}

	if (col.type === "date" || isDateColumnKey(col.key)) {
		return formatCsvDateValue(value);
	}

	if (col.type === "multi" || isMultiTextColumnKey(col.key)) {
		const parts = splitMultiValues(value);
		return parts.length ? parts.join(", ") : "";
	}

	if (value === null || value === undefined || value === "" || value === "-") return "";
	return String(value);
};

const downloadTableCsv = (rows, fileName) => {
	if (!rows?.length) return;

	const csvRows = [
		COLUMNS.map((col) => toCsvValue(col.label)).join(","),
		...rows.map((row) => COLUMNS.map((col) => toCsvValue(getExportCellValue(row, col))).join(",")),
	];

	const blob = new Blob([csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
	const url = URL.createObjectURL(blob);
	const link = document.createElement("a");
	link.href = url;
	link.setAttribute("download", fileName);
	document.body.appendChild(link);
	link.click();
	document.body.removeChild(link);
	URL.revokeObjectURL(url);
};

const datePickerSx = {
	width: "100%",
	minWidth: 170,
	"& .MuiInputBase-root": { height: 40, borderRadius: 1.5 },
	"& .MuiInputBase-input": { fontSize: 13 },
};

const filterItemSx = {
	flex: { xs: "1 1 100%", sm: "1 1 170px" },
	minWidth: { xs: "100%", sm: 170 },
	maxWidth: { sm: 220 },
};

const PINNED_COLUMNS = [
	{ key: "order_name", label: "Order Name", width: 160 },
];

const SHOPIFY_COLUMNS = [
	{ key: "kind", label: "Kind", width: 90 },
	{ key: "gateway", label: "Gateway", width: 110 },
	{ key: "status", label: "Status", width: 90 },
	{ key: "amount", label: "Amount", width: 100, align: "right", type: "currency" },
	{ key: "processed_at", label: "Processed At", width: 150, type: "date" },
	{ key: "created_at", label: "Created At", width: 150, type: "date" },
	{ key: "payment_id", label: "Payment ID", width: 160, type: "multi" },
	{ key: "refund_id", label: "Refund ID", width: 170, type: "multi" },
];

const PARTNER_COLUMNS = [
	{ key: "partner_type", label: "Partner Type", width: 120 },
	{ key: "pay_id", label: "Pay ID", width: 160, type: "multi" },
	{ key: "partner_refund_id", label: "Partner Refund ID", width: 170, type: "multi" },
	{ key: "refund_amt", label: "Refund Amt", width: 110, align: "right", type: "currency" },
	{ key: "bank_arn", label: "Bank ARN", width: 150, type: "multi" },
	{ key: "bank_rrn", label: "Bank RRN", width: 150, type: "multi" },
	{ key: "refund_at", label: "Refund At", width: 150, type: "date" },
	{ key: "match_status", label: "Match", width: 110, align: "center" },
];

const COLUMNS = [...PINNED_COLUMNS, ...SHOPIFY_COLUMNS, ...PARTNER_COLUMNS];

const MONTH_NAME_TO_INDEX = {
	jan: 1,
	january: 1,
	feb: 2,
	february: 2,
	mar: 3,
	march: 3,
	apr: 4,
	april: 4,
	may: 5,
	jun: 6,
	june: 6,
	jul: 7,
	july: 7,
	aug: 8,
	august: 8,
	sep: 9,
	sept: 9,
	september: 9,
	oct: 10,
	october: 10,
	nov: 11,
	november: 11,
	dec: 12,
	december: 12,
};

const unwrapSortValue = (value) => {
	if (value === null || value === undefined) return "";
	if (typeof value === "object") {
		return value.value ?? value.date ?? value.datetime ?? value.timestamp ?? "";
	}
	return value;
};

const parseSingleDateToMs = (raw) => {
	if (raw === null || raw === undefined || raw === "") return null;
	let text = String(raw).trim();
	if (!text || text === "-" || text.toLowerCase() === "null" || text.toLowerCase() === "none") return null;

	text = text.replace(/\s+/g, " ").replace(/ UTC$/i, "Z");

	// DD/MM/YYYY or D/M/YYYY
	const dmy = text.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})(?:\b.*)?$/);
	if (dmy) {
		const parsed = dayjs(`${dmy[3]}-${dmy[2].padStart(2, "0")}-${dmy[1].padStart(2, "0")}`);
		return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
	}

	// DD MMM YYYY / DD Month YYYY (display format in table)
	const dMonY = text.match(/^(\d{1,2})\s+([A-Za-z]{3,})\s+(\d{4})(?:\b.*)?$/);
	if (dMonY) {
		const month = MONTH_NAME_TO_INDEX[dMonY[2].toLowerCase()];
		if (month) {
			const parsed = dayjs(
				`${dMonY[3]}-${String(month).padStart(2, "0")}-${dMonY[1].padStart(2, "0")}`,
			);
			return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
		}
	}

	// YYYY-MM-DD or ISO / BigQuery timestamps — use calendar date only
	const iso = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
	if (iso) {
		const parsed = dayjs(`${iso[1]}-${iso[2]}-${iso[3]}`);
		return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
	}

	const cleaned = text.replace(/[+-]\d{2}:?\d{0,2}$/, "").trim();
	let parsed = dayjs(cleaned);
	if (!parsed.isValid()) parsed = dayjs(text);
	return parsed.isValid() ? parsed.startOf("day").valueOf() : null;
};

const parseDateFieldToMs = (value) => {
	const raw = unwrapSortValue(value);
	if (raw === null || raw === undefined || raw === "" || raw === "-") return null;

	const text = String(raw).trim();
	const parts = text.includes(",")
		? text
				.split(",")
				.map((part) => part.trim())
				.filter(Boolean)
		: [text];

	const timestamps = parts.map(parseSingleDateToMs).filter((ms) => ms !== null);
	if (!timestamps.length) return null;

	// Sort by earliest date in the cell for stable ordering.
	return Math.min(...timestamps);
};

const getSortComparableValue = (row, col) => {
	const value = row?.[col.key];
	if (value === null || value === undefined || value === "" || value === "-") return null;

	if (col.type === "currency") return toNumber(value);

	if (col.type === "date" || isDateColumnKey(col.key)) {
		return parseDateFieldToMs(value);
	}

	return String(unwrapSortValue(value)).toLowerCase();
};

const compareSortValues = (a, b, direction = "asc") => {
	if (a === null && b === null) return 0;
	if (a === null) return 1;
	if (b === null) return -1;

	let result = 0;
	if (typeof a === "number" && typeof b === "number") {
		result = a - b;
	} else {
		result = String(a).localeCompare(String(b), undefined, { numeric: true, sensitivity: "base" });
	}
	return direction === "desc" ? -result : result;
};

const getPinnedLeft = (index) => PINNED_COLUMNS.slice(0, index).reduce((sum, col) => sum + col.width, 0);

const pinnedCellSx = (index, isHeader = false, rowBg = "#ffffff") => ({
	position: "sticky",
	left: getPinnedLeft(index),
	zIndex: isHeader ? 5 : 2,
	minWidth: PINNED_COLUMNS[index].width,
	width: PINNED_COLUMNS[index].width,
	maxWidth: PINNED_COLUMNS[index].width,
	backgroundColor: isHeader ? "#dbeafe" : rowBg,
	borderRight: index === PINNED_COLUMNS.length - 1 ? "2px solid #93c5fd" : "1px solid #bfdbfe",
	boxShadow: index === PINNED_COLUMNS.length - 1 ? "4px 0 8px -4px rgba(15, 23, 42, 0.12)" : "none",
	fontWeight: isHeader ? 700 : 400,
	fontSize: 12,
	py: 1.25,
	px: 1,
	verticalAlign: "middle",
	top: isHeader ? 0 : undefined,
});

const groupHeaderSx = (bg, color, borderColor) => ({
	backgroundColor: bg,
	color,
	fontWeight: 700,
	fontSize: 12,
	borderBottom: `1px solid ${borderColor}`,
	borderRight: `1px solid ${borderColor}`,
	py: 0.75,
	px: 1,
	textAlign: "center",
	top: 0,
	zIndex: 3,
});

const headerCellSx = (variant = "shopify") => {
	const styles =
		variant === "partner"
			? { backgroundColor: "#f5f3ff", borderBottom: "1px solid #ddd6fe", borderRight: "1px solid #ddd6fe" }
			: variant === "pinned"
				? { backgroundColor: "#dbeafe", borderBottom: "1px solid #bfdbfe", borderRight: "1px solid #bfdbfe" }
				: { backgroundColor: "#e0f2fe", borderBottom: "1px solid #7dd3fc", borderRight: "1px solid #7dd3fc" };

	return {
		...styles,
		fontWeight: 700,
		fontSize: 12,
		py: 1.25,
		px: 1,
		whiteSpace: "nowrap",
		top: 36,
	};
};

const bodyCellSx = (align = "left", variant = "default") => ({
	fontSize: 12,
	borderBottom: "1px solid #eceff1",
	borderRight: "1px solid #eceff1",
	py: 1,
	px: 1,
	textAlign: align,
	verticalAlign: "middle",
	backgroundColor: variant === "shopify" ? "#f8fbff" : variant === "partner" ? "#faf5ff" : undefined,
});

const SHOPIFY_COLUMN_META = SHOPIFY_COLUMNS.map((col) => ({
	col,
	sx: bodyCellSx(col.align || "left", "shopify"),
}));

const PARTNER_COLUMN_META = PARTNER_COLUMNS.map((col) => ({
	col,
	sx: bodyCellSx(col.align || "left", "partner"),
}));

const PINNED_ORDER_NAME_SX = {
	...pinnedCellSx(0, false, "#ffffff"),
	left: 0,
	position: "sticky",
	zIndex: 2,
	verticalAlign: "middle",
	textAlign: "left",
	fontWeight: 600,
	backgroundColor: "#ffffff",
	backgroundClip: "padding-box",
	boxShadow: "4px 0 8px -4px rgba(15, 23, 42, 0.12)",
};

const PINNED_ORDER_NAME_SPAN_SX = {
	...PINNED_ORDER_NAME_SX,
	zIndex: 4,
};

const TABLE_ROW_SX_EVEN = {
	backgroundColor: "#ffffff",
	"&:hover": { backgroundColor: "#eff6ff" },
	"&:hover .pinned-cell": { backgroundColor: "#eff6ff" },
};

const TABLE_ROW_SX_ODD = {
	backgroundColor: "#f8fafc",
	"&:hover": { backgroundColor: "#eff6ff" },
	"&:hover .pinned-cell": { backgroundColor: "#eff6ff" },
};

const ROLE_TAG_STYLES = {
	VIEWER: { backgroundColor: "#f1f5f9", color: "#475569" },
	EDITOR: { backgroundColor: "#fffbeb", color: "#b45309" },
	PUBLISHER: { backgroundColor: "#f5f3ff", color: "#7c3aed" },
	ADMIN: { backgroundColor: "#ecfdf5", color: "#047857" },
};

const GATEWAY_STYLES = {
	payu: { backgroundColor: "#dbeafe", color: "#1e40af" },
	razorpay: { backgroundColor: "#f5f3ff", color: "#7c3aed" },
	snapmint: { backgroundColor: "#ecfdf5", color: "#047857" },
	giftcard: { backgroundColor: "#fff7ed", color: "#c2410c" },
};

const isGiftCardRow = (row) => {
	if (row?._gatewayKey === "giftcard") return true;
	const candidates = [row?.partner_type, row?.gateway, row?.partnerType]
		.map((value) => String(value ?? "").trim().toLowerCase())
		.filter((value) => value && value !== "-" && value !== "null" && value !== "undefined" && value !== "nan");
	return candidates.some((value) => value.includes("gift"));
};

const resolveGatewayKey = (partner_type, gateway) => {
	const candidates = [partner_type, gateway]
		.map((value) => String(value ?? "").trim().toLowerCase())
		.filter((value) => value && value !== "-" && value !== "null" && value !== "undefined" && value !== "nan");

	for (const value of candidates) {
		if (value.includes("payu")) return "payu";
		if (value.includes("razorpay")) return "razorpay";
		if (value.includes("snapmint")) return "snapmint";
		if (value.includes("gift")) return "giftcard";
	}
	return "other";
};

const hasPresentValue = (value) => {
	if (value === null || value === undefined || value === "") return false;
	const text = String(value).trim();
	if (!text || text === "-") return false;
	const lower = text.toLowerCase();
	return lower !== "null" && lower !== "none" && lower !== "undefined" && lower !== "nan";
};

const getMatchStatus = (row, gatewayKey) => {
	if ((gatewayKey || row?._gatewayKey) === "giftcard" || isGiftCardRow(row)) return "Gift Card";
	const hasShopify = hasPresentValue(row?.order_id) || hasPresentValue(row?.payment_id);
	const hasPartner = hasPresentValue(row?.pay_id) || toNumber(row?.refund_amt) !== 0;
	if (hasShopify && hasPartner) return "Matched";
	if (hasShopify) return "Shopify Only";
	if (hasPartner) return "Partner Only";
	return "Unknown";
};

const formatMultiDisplay = (value) => {
	const parts = splitMultiValues(value);
	return parts.length ? parts.join(", ") : "-";
};

const normalizeRows = (data = []) => {
	const seen = new Set();
	const normalized = [];

	for (let index = 0; index < data.length; index += 1) {
		const row = data[index];
		const order_id = toSafeText(row?.order_id);
		const order_name = toSafeText(row?.order_name ?? row?.orderName);
		const kind = toSafeText(row?.kind);
		const gateway = toSafeText(row?.gateway);
		const partner_type = toSafeText(row?.partner_type ?? row?.partnerType);
		const status = toSafeText(row?.status);
		const payment_id = toSafeText(row?.payment_id);
		const refund_id = toSafeText(row?.refund_id);
		const pay_id = toSafeText(row?.pay_id);
		const bank_arn = toSafeText(row?.bank_arn);
		const bank_rrn = toSafeText(row?.bank_rrn);
		const partner_refund_id = toSafeText(row?.partner_refund_id);
		const receipt = toSafeText(row?.receipt);
		const amount = toNumber(row?.amount);
		const refund_amt = toNumber(row?.refund_amt);
		const gatewayKey = resolveGatewayKey(partner_type, gateway);
		const match_status =
			gatewayKey === "giftcard"
				? "Gift Card"
				: getMatchStatus(
						{
							order_id,
							payment_id,
							pay_id,
							refund_amt,
							gateway,
							partner_type,
						},
						gatewayKey,
					);

		const dedupeKey = [
			order_id,
			order_name,
			gateway,
			partner_type,
			payment_id,
			refund_id,
			pay_id,
			partner_refund_id,
			amount,
			refund_amt,
			String(row?.processed_at ?? ""),
			String(row?.created_at ?? ""),
			String(row?.refund_at ?? ""),
		].join("|");

		if (seen.has(dedupeKey)) continue;
		seen.add(dedupeKey);

		const orderNameKey = order_name && order_name !== "-" ? order_name : "";

		normalized.push({
			id: row?.id ?? `${order_id || "na"}-${payment_id || pay_id || index}-${normalized.length}`,
			order_id,
			order_name,
			kind,
			gateway,
			partner_type,
			status,
			amount,
			processed_at: row?.processed_at,
			created_at: row?.created_at,
			payment_id,
			refund_id,
			pay_id,
			refund_amt,
			bank_arn,
			bank_rrn,
			refund_at: row?.refund_at,
			partner_refund_id,
			receipt,
			match_status,
			_gatewayKey: gatewayKey,
			_orderNameKey: orderNameKey,
			_display: {
				amount: formatCurrency(amount),
				refund_amt: formatCurrency(refund_amt),
				processed_at: formatDateValue(row?.processed_at),
				created_at: formatDateValue(row?.created_at),
				refund_at: formatDateValue(row?.refund_at),
				payment_id: formatMultiDisplay(payment_id),
				refund_id: formatMultiDisplay(refund_id),
				pay_id: formatMultiDisplay(pay_id),
				partner_refund_id: formatMultiDisplay(partner_refund_id),
				bank_arn: formatMultiDisplay(bank_arn),
				bank_rrn: formatMultiDisplay(bank_rrn),
			},
			_searchHaystack: [
				order_id,
				order_name,
				kind,
				gateway,
				partner_type,
				status,
				payment_id,
				refund_id,
				pay_id,
				partner_refund_id,
				bank_arn,
				bank_rrn,
				receipt,
				match_status,
			]
				.join(" ")
				.toLowerCase(),
		});
	}

	return normalized;
};

const renderGateway = (value) => {
	const key = String(value || "").toLowerCase();
	let style = { backgroundColor: "#f3f4f6", color: "#374151" };
	if (key.includes("payu")) style = GATEWAY_STYLES.payu;
	else if (key.includes("razorpay")) style = GATEWAY_STYLES.razorpay;
	else if (key.includes("snapmint")) style = GATEWAY_STYLES.snapmint;
	else if (key.includes("gift")) style = GATEWAY_STYLES.giftcard;

	return (
		<Chip size="small" label={value || "-"} sx={{ fontWeight: 600, fontSize: 11, height: 24, ...style }} />
	);
};

const MATCH_STATUS_STYLES = {
	Matched: { backgroundColor: "#ecfdf5", color: "#047857" },
	"Shopify Only": { backgroundColor: "#dbeafe", color: "#1e40af" },
	"Partner Only": { backgroundColor: "#fffbeb", color: "#b45309" },
	"Gift Card": { backgroundColor: "#fff7ed", color: "#c2410c" },
	Unknown: { backgroundColor: "#f3f4f6", color: "#374151" },
};

const renderMatchStatus = (value) => {
	const chipStyle = MATCH_STATUS_STYLES[value] || MATCH_STATUS_STYLES.Unknown;
	return (
		<Chip size="small" label={value} sx={{ fontWeight: 600, fontSize: 11, height: 24, ...chipStyle }} />
	);
};

const multiTextSx = { fontSize: 12, color: "#334155", whiteSpace: "normal", wordBreak: "break-word", lineHeight: 1.35 };

const renderMultiValue = (value, preformatted) => {
	const text = preformatted ?? formatMultiDisplay(value);
	if (!text || text === "-") return "-";
	return (
		<Typography variant="body2" title={text} sx={multiTextSx}>
			{text}
		</Typography>
	);
};

const renderDateValue = (value, preformatted) => {
	const formatted = preformatted ?? formatDateValue(value);
	if (formatted === "-") return "-";
	return (
		<Typography variant="body2" title={formatted} sx={multiTextSx}>
			{formatted}
		</Typography>
	);
};

const renderCurrency = (value, preformatted) => (
	<Typography variant="body2" fontWeight={600} color="#0f766e" sx={{ fontSize: 12 }}>
		{preformatted ?? formatCurrency(value)}
	</Typography>
);

const renderCellValue = (row, col) => {
	const display = row._display || {};
	if (col.type === "currency") return renderCurrency(row[col.key], display[col.key]);
	if (col.type === "date") return renderDateValue(row[col.key], display[col.key]);
	if (col.type === "multi" || isMultiTextColumnKey(col.key)) return renderMultiValue(row[col.key], display[col.key]);
	if (col.key === "gateway" || col.key === "partner_type") return renderGateway(row[col.key]);
	if (col.key === "match_status") return renderMatchStatus(row.match_status);
	return row[col.key] || "-";
};

const cellTextSx = {
	fontSize: 12,
	color: "#334155",
	whiteSpace: "normal",
	wordBreak: "break-word",
	lineHeight: 1.35,
};

const renderDataCellContent = (row, col) => {
	if (col.align === "right") {
		return <Box display="flex" justifyContent="flex-end">{renderCellValue(row, col)}</Box>;
	}
	if (col.align === "center") {
		return <Box display="flex" justifyContent="center">{renderCellValue(row, col)}</Box>;
	}
	if (
		col.type === "date" ||
		col.type === "multi" ||
		isMultiTextColumnKey(col.key) ||
		col.key === "gateway" ||
		col.key === "partner_type" ||
		col.key === "match_status"
	) {
		return renderCellValue(row, col);
	}
	return (
		<Typography
			variant="body2"
			title={String(row[col.key] ?? "")}
			sx={{ ...cellTextSx, fontWeight: col.key === "order_name" ? 600 : 400 }}>
			{row[col.key] || "-"}
		</Typography>
	);
};

const GATEWAYS = [
	{ key: "payu", label: "PayU", color: "#1e40af", bar: "#3b82f6", bg: "#eff6ff" },
	{ key: "razorpay", label: "Razorpay", color: "#7c3aed", bar: "#8b5cf6", bg: "#f5f3ff" },
	{ key: "snapmint", label: "Snapmint", color: "#047857", bar: "#10b981", bg: "#ecfdf5" },
	{ key: "giftcard", label: "Gift Card", color: "#c2410c", bar: "#f97316", bg: "#fff7ed" },
	{ key: "other", label: "Other", color: "#64748b", bar: "#94a3b8", bg: "#f8fafc" },
];

const MAIN_GATEWAYS = GATEWAYS.filter((g) => g.key !== "other");
const GATEWAY_LABEL_BY_KEY = Object.fromEntries(GATEWAYS.map((g) => [g.key, g.label]));

const MATCH_FILTER_ALL_SX = { backgroundColor: "#dbeafe", color: "#1e40af" };
const MATCH_FILTER_MATCHED_SX = { backgroundColor: "#ecfdf5", color: "#047857" };
const MATCH_FILTER_MISMATCHED_SX = { backgroundColor: "#fffbeb", color: "#b45309" };
const MATCH_FILTER_SHOPIFY_ONLY_SX = { backgroundColor: "#dbeafe", color: "#1e40af" };
const MATCH_FILTER_PARTNER_ONLY_SX = { backgroundColor: "#f5f3ff", color: "#7c3aed" };

const getGatewayKey = (row) => {
	if (row?._gatewayKey) return row._gatewayKey;
	return resolveGatewayKey(row?.partner_type ?? row?.partnerType, row?.gateway);
};

/** Consecutive same order_name (any gateway) → first index gets rowspan, later indices are 0 (omit cell). */
const getOrderNameRowSpans = (rows = []) => {
	const spans = new Array(rows.length).fill(1);
	let i = 0;
	while (i < rows.length) {
		const name = rows[i]?._orderNameKey || String(rows[i]?.order_name ?? "").trim();
		const canSpan = Boolean(name) && name !== "-";
		if (!canSpan) {
			spans[i] = 1;
			i += 1;
			continue;
		}
		let j = i + 1;
		while (j < rows.length) {
			const nextName = rows[j]?._orderNameKey || String(rows[j]?.order_name ?? "").trim();
			if (nextName !== name) break;
			j += 1;
		}
		spans[i] = j - i;
		for (let k = i + 1; k < j; k += 1) spans[k] = 0;
		i = j;
	}
	return spans;
};

const clusterByOrderName = (list) => {
	const groups = new Map();
	const anchors = [];

	for (let i = 0; i < list.length; i += 1) {
		const row = list[i];
		const orderName = row._orderNameKey || String(row?.order_name ?? "").trim();
		const key = orderName && orderName !== "-" ? orderName : `__unique_${row.id}`;
		let group = groups.get(key);
		if (!group) {
			group = [];
			groups.set(key, group);
			anchors.push(key);
		}
		group.push(row);
	}

	const result = [];
	for (let a = 0; a < anchors.length; a += 1) {
		const group = groups.get(anchors[a]);
		if (group.length > 1) {
			group.sort((left, right) => {
				const byGateway = (left._gatewayKey || "").localeCompare(right._gatewayKey || "");
				if (byGateway !== 0) return byGateway;
				return String(left?.id ?? "").localeCompare(String(right?.id ?? ""));
			});
		}
		for (let g = 0; g < group.length; g += 1) result.push(group[g]);
	}
	return result;
};

const orderNameTextSx = { ...cellTextSx, fontWeight: 600 };

const RefundTableRow = memo(function RefundTableRow({ row, index, orderNameSpan }) {
	const isSpanned = orderNameSpan > 1;
	return (
		<TableRow hover sx={index % 2 === 0 ? TABLE_ROW_SX_EVEN : TABLE_ROW_SX_ODD}>
			{orderNameSpan > 0 && (
				<TableCell
					align="left"
					rowSpan={isSpanned ? orderNameSpan : undefined}
					className="pinned-cell"
					sx={isSpanned ? PINNED_ORDER_NAME_SPAN_SX : PINNED_ORDER_NAME_SX}>
					<Typography variant="body2" title={String(row.order_name ?? "")} sx={orderNameTextSx}>
						{row.order_name || "-"}
					</Typography>
				</TableCell>
			)}
			{SHOPIFY_COLUMN_META.map(({ col, sx }) => (
				<TableCell key={col.key} align={col.align || "left"} sx={sx}>
					{renderDataCellContent(row, col)}
				</TableCell>
			))}
			{PARTNER_COLUMN_META.map(({ col, sx }) => (
				<TableCell key={col.key} align={col.align || "left"} sx={sx}>
					{renderDataCellContent(row, col)}
				</TableCell>
			))}
		</TableRow>
	);
});

const FilterPill = memo(function FilterPill({ label, active, onClick, activeSx }) {
	return (
		<Chip
			label={label}
			size="small"
			onClick={onClick}
			sx={{
				fontWeight: 600,
				cursor: "pointer",
				...(active
					? activeSx
					: { backgroundColor: "#f1f5f9", color: "#475569", border: "1px solid #e2e8f0" }),
			}}
		/>
	);
});

const InfoStat = ({ label, value, sub, color = "#1e293b" }) => (
	<Box sx={{ minWidth: 120 }}>
		<Typography variant="caption" color="text.secondary" fontWeight={600} display="block">
			{label}
		</Typography>
		<Typography variant="subtitle1" fontWeight={700} sx={{ color, lineHeight: 1.2 }}>
			{value}
		</Typography>
		{sub && (
			<Typography variant="caption" color="text.secondary">
				{sub}
			</Typography>
		)}
	</Box>
);

const GatewaySplitCard = memo(function GatewaySplitCard({
	label,
	color,
	barColor,
	bg,
	stats,
	total,
	active = false,
	onClick,
	hideGaps = false,
}) {
	const count = stats?.count || 0;
	const amount = stats?.amount || 0;
	const matched = stats?.matched || 0;
	const shopifyOnly = stats?.shopifyOnly || 0;
	const partnerOnly = stats?.partnerOnly || 0;
	const gaps = shopifyOnly + partnerOnly;
	const pct = total ? Math.round((count / total) * 100) : 0;
	const matchPct = count ? Math.round((matched / count) * 100) : 0;

	return (
		<Box
			role="button"
			tabIndex={0}
			onClick={onClick}
			onKeyDown={(event) => {
				if (event.key === "Enter" || event.key === " ") {
					event.preventDefault();
					onClick?.(event);
				}
			}}
			sx={{
				flex: 1,
				minWidth: 0,
				p: 1.5,
				borderRadius: 2,
				border: active ? `2px solid ${barColor}` : `1px solid ${barColor}55`,
				background: `linear-gradient(160deg, ${bg} 0%, #ffffff 72%)`,
				cursor: "pointer",
				boxShadow: active ? `0 0 0 3px ${barColor}33` : "none",
				transition: "border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease",
				"&:hover": {
					borderColor: barColor,
					transform: "translateY(-1px)",
				},
			}}>
			<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
				<Typography variant="body2" fontWeight={700} sx={{ color }}>
					{label}
				</Typography>
				<Chip
					size="small"
					label={`${pct}%`}
					sx={{ height: 22, fontWeight: 700, fontSize: 11, backgroundColor: `${barColor}22`, color }}
				/>
			</Stack>

			<Typography variant="h6" fontWeight={700} sx={{ color, lineHeight: 1.1, mb: 0.25 }}>
				{count}
			</Typography>
			<Typography variant="caption" color="text.secondary" display="block" mb={1}>
				{formatCurrency(amount)} total
			</Typography>

			<Box sx={{ height: 6, borderRadius: 99, backgroundColor: "#e2e8f0", overflow: "hidden", mb: 1.25 }}>
				<Box sx={{ width: `${pct}%`, height: "100%", backgroundColor: barColor, borderRadius: 99 }} />
			</Box>

			{hideGaps ? (
				<Box>
					<Typography variant="caption" color="text.secondary" display="block">
						Refunds
					</Typography>
					<Typography variant="body2" fontWeight={700} sx={{ color }}>
						{count}
					</Typography>
					<Typography variant="caption" color="text.secondary">
						No partner matching
					</Typography>
				</Box>
			) : (
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: "1fr 1fr",
						gap: 0.75,
					}}>
					<Box>
						<Typography variant="caption" color="text.secondary" display="block">
							Matched
						</Typography>
						<Typography variant="body2" fontWeight={700} color="#047857">
							{matched}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{matchPct}% · {formatCurrency(stats?.matchedAmt || 0)}
						</Typography>
					</Box>
					<Box>
						<Typography variant="caption" color="text.secondary" display="block">
							Gaps
						</Typography>
						<Typography variant="body2" fontWeight={700} color="#b45309">
							{gaps}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{formatCurrency((stats?.shopifyOnlyAmt || 0) + (stats?.partnerOnlyAmt || 0))}
						</Typography>
					</Box>
					<Box>
						<Typography variant="caption" color="text.secondary" display="block">
							Shopify Only
						</Typography>
						<Typography variant="body2" fontWeight={700} color="#1e40af">
							{shopifyOnly}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{formatCurrency(stats?.shopifyOnlyAmt || 0)}
						</Typography>
					</Box>
					<Box>
						<Typography variant="caption" color="text.secondary" display="block">
							Partner Only
						</Typography>
						<Typography variant="body2" fontWeight={700} color="#7c3aed">
							{partnerOnly}
						</Typography>
						<Typography variant="caption" color="text.secondary">
							{formatCurrency(stats?.partnerOnlyAmt || 0)}
						</Typography>
					</Box>
				</Box>
			)}
		</Box>
	);
});

const emptyGatewayStats = () =>
	GATEWAYS.reduce((acc, g) => {
		acc[g.key] = {
			count: 0,
			amount: 0,
			matched: 0,
			matchedAmt: 0,
			shopifyOnly: 0,
			shopifyOnlyAmt: 0,
			partnerOnly: 0,
			partnerOnlyAmt: 0,
		};
		return acc;
	}, {});

export default function RefundTrackerDashboard() {
	const [rows, setRows] = useState([]);
	const [search, setSearch] = useState("");
	const deferredSearch = useDeferredValue(search);
	const [matchFilter, setMatchFilter] = useState("all");
	const [gatewayFilter, setGatewayFilter] = useState([]);
	const [sortBy, setSortBy] = useState("");
	const [sortDir, setSortDir] = useState("asc");
	const [fromDate, setFromDate] = useState(dayjs().startOf("month"));
	const [toDate, setToDate] = useState(dayjs());
	const [rangeError, setRangeError] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(25);
	const isSearchPending = search !== deferredSearch;
	const { setLoadingOpen, setSnackBar, setSnackBarOpen } = GlobalState();
	const { user } = AuthState();
	const roleData = refundTrackerRoleAccess(user);
	const canDownload = canDownloadRefundTracker(roleData.roles);
	const gatewayFilterSet = useMemo(() => new Set(gatewayFilter), [gatewayFilter]);

	useEffect(() => {
		const fetchRefunds = async () => {
			if (!fromDate?.isValid() || !toDate?.isValid()) {
				setRows([]);
				return;
			}
			if (fromDate.isAfter(toDate, "day")) {
				setRangeError("From date must be earlier than or equal to To date");
				setRows([]);
				return;
			}

			setRangeError("");
			try {
				setLoadingOpen(true);
				const res = await pythonVmInstance({
					url: "/refund-tracker/dashboard",
					method: "GET",
					headers: { "Content-Type": "application/json" },
					params: {
						start_date: fromDate.format("YYYY-MM-DD"),
						end_date: toDate.format("YYYY-MM-DD"),
					},
				});
				const data = Array.isArray(res?.data) ? res.data : res?.data?.rows || res?.data?.data || [];
				setRows(normalizeRows(data));
			} catch {
				setRows([]);
				setSnackBarOpen(true);
				setSnackBar({
					message: "Failed to fetch refund tracker data.",
					severity: "warning",
					anchorOrigin: { horizontal: "center", vertical: "top" },
				});
			} finally {
				setLoadingOpen(false);
			}
		};

		fetchRefunds();
	}, [fromDate, toDate, setLoadingOpen, setSnackBar, setSnackBarOpen]);

	useEffect(() => {
		setPage(0);
	}, [deferredSearch, matchFilter, gatewayFilter, sortBy, sortDir, fromDate, toDate, rows]);

	const searchFilteredRows = useMemo(() => {
		const query = deferredSearch.trim().toLowerCase();
		if (!query) return rows;
		return rows.filter((row) => (row._searchHaystack || "").includes(query));
	}, [rows, deferredSearch]);

	const gatewayFilteredRows = useMemo(() => {
		if (!gatewayFilter.length) return searchFilteredRows;
		const selected = new Set(gatewayFilter);
		return searchFilteredRows.filter((row) => selected.has(row._gatewayKey || getGatewayKey(row)));
	}, [searchFilteredRows, gatewayFilter]);

	const filteredRows = useMemo(() => {
		if (matchFilter === "matched") {
			return gatewayFilteredRows.filter((row) => row.match_status === "Matched");
		}
		if (matchFilter === "mismatched") {
			return gatewayFilteredRows.filter(
				(row) => row.match_status !== "Matched" && row.match_status !== "Gift Card",
			);
		}
		if (matchFilter === "shopify_only") {
			return gatewayFilteredRows.filter((row) => row.match_status === "Shopify Only");
		}
		if (matchFilter === "partner_only") {
			return gatewayFilteredRows.filter((row) => row.match_status === "Partner Only");
		}
		return gatewayFilteredRows;
	}, [gatewayFilteredRows, matchFilter]);

	const tableFilterCounts = useMemo(() => {
		let total = 0;
		let matched = 0;
		let mismatched = 0;
		let shopifyOnly = 0;
		let partnerOnly = 0;
		for (let i = 0; i < gatewayFilteredRows.length; i += 1) {
			const status = gatewayFilteredRows[i].match_status;
			total += 1;
			if (status === "Matched") matched += 1;
			else if (status === "Shopify Only") {
				shopifyOnly += 1;
				mismatched += 1;
			} else if (status === "Partner Only") {
				partnerOnly += 1;
				mismatched += 1;
			} else if (status !== "Gift Card") mismatched += 1;
		}
		return { total, matched, mismatched, shopifyOnly, partnerOnly };
	}, [gatewayFilteredRows]);

	const sortedRows = useMemo(() => {
		if (!sortBy) return clusterByOrderName(filteredRows);

		const col = COLUMNS.find((item) => item.key === sortBy);
		if (!col) return clusterByOrderName(filteredRows);

		const decorated = new Array(filteredRows.length);
		for (let i = 0; i < filteredRows.length; i += 1) {
			const row = filteredRows[i];
			decorated[i] = {
				row,
				sortVal: getSortComparableValue(row, col),
				orderName: row._orderNameKey || String(row?.order_name ?? ""),
			};
		}
		decorated.sort((left, right) => {
			const primary = compareSortValues(left.sortVal, right.sortVal, sortDir);
			if (primary !== 0) return primary;
			return left.orderName.localeCompare(right.orderName, undefined, {
				numeric: true,
				sensitivity: "base",
			});
		});

		const ordered = new Array(decorated.length);
		for (let i = 0; i < decorated.length; i += 1) ordered[i] = decorated[i].row;
		return clusterByOrderName(ordered);
	}, [filteredRows, sortBy, sortDir]);

	const paginatedRows = useMemo(() => {
		const start = page * rowsPerPage;
		return sortedRows.slice(start, start + rowsPerPage);
	}, [sortedRows, page, rowsPerPage]);

	// Must recompute on the current page so rowspan stays correct after pagination.
	const orderNameRowSpans = useMemo(() => getOrderNameRowSpans(paginatedRows), [paginatedRows]);

	const handleSort = useCallback((columnKey) => {
		if (sortBy === columnKey) {
			if (sortDir === "asc") {
				setSortDir("desc");
			} else {
				setSortBy("");
				setSortDir("asc");
			}
			return;
		}
		setSortBy(columnKey);
		setSortDir("asc");
	}, [sortBy, sortDir]);

	const renderSortableHeader = (col, extraSx = {}) => (
		<TableCell
			key={col.key}
			align={col.align || "left"}
			sortDirection={sortBy === col.key ? sortDir : false}
			sx={{
				...extraSx,
				minWidth: col.width,
				width: col.width,
				cursor: "pointer",
				userSelect: "none",
			}}
			onClick={() => handleSort(col.key)}>
			<TableSortLabel
				active={sortBy === col.key}
				direction={sortBy === col.key ? sortDir : "asc"}
				sx={{
					width: "100%",
					justifyContent: col.align === "right" ? "flex-end" : col.align === "center" ? "center" : "flex-start",
					"& .MuiTableSortLabel-icon": { fontSize: 16 },
					color: "inherit !important",
					fontWeight: 700,
					fontSize: 12,
				}}>
				{col.label}
			</TableSortLabel>
		</TableCell>
	);

	const summary = useMemo(() => {
		const acc = {
			shopifyAmount: 0,
			partnerAmount: 0,
			total: 0,
			matched: 0,
			mismatched: 0,
			shopifyOnly: 0,
			partnerOnly: 0,
			shopifyOnlyAmt: 0,
			partnerOnlyAmt: 0,
			gatewayStats: emptyGatewayStats(),
		};

		// Gateway split cards use search-scoped rows so filter chips still show full distribution.
		for (let i = 0; i < searchFilteredRows.length; i += 1) {
			const row = searchFilteredRows[i];
			const shopifyAmt = row.amount || 0;
			const partnerAmt = row.refund_amt || 0;
			const gateway = row._gatewayKey || getGatewayKey(row);
			const rowAmt = partnerAmt || shopifyAmt;
			const gStats = acc.gatewayStats[gateway] || acc.gatewayStats.other;

			gStats.count += 1;
			gStats.amount += rowAmt;

			if (row.match_status === "Gift Card" || gateway === "giftcard") {
				gStats.matched += 1;
				gStats.matchedAmt += shopifyAmt;
			} else if (row.match_status === "Matched") {
				gStats.matched += 1;
				gStats.matchedAmt += shopifyAmt || partnerAmt;
			} else if (row.match_status === "Shopify Only") {
				gStats.shopifyOnly += 1;
				gStats.shopifyOnlyAmt += shopifyAmt;
			} else if (row.match_status === "Partner Only") {
				gStats.partnerOnly += 1;
				gStats.partnerOnlyAmt += partnerAmt;
			}
		}

		return acc;
	}, [searchFilteredRows]);

	// KPI strip reflects the rows currently shown in the table (search + gateway + match filters).
	const tableSummary = useMemo(() => {
		const acc = {
			shopifyAmount: 0,
			partnerAmount: 0,
			// Amount Diff only compares gateways that have partner matching (exclude gift cards).
			diffShopifyAmount: 0,
			diffPartnerAmount: 0,
			total: 0,
			matched: 0,
			mismatched: 0,
			shopifyOnly: 0,
			partnerOnly: 0,
			giftCardCount: 0,
		};

		for (let i = 0; i < filteredRows.length; i += 1) {
			const row = filteredRows[i];
			const shopifyAmt = row.amount || 0;
			const partnerAmt = row.refund_amt || 0;
			const gateway = row._gatewayKey || getGatewayKey(row);
			const isGiftCard = row.match_status === "Gift Card" || gateway === "giftcard";

			acc.shopifyAmount += shopifyAmt;
			acc.partnerAmount += partnerAmt;
			acc.total += 1;

			if (isGiftCard) {
				acc.giftCardCount += 1;
			} else {
				acc.diffShopifyAmount += shopifyAmt;
				acc.diffPartnerAmount += partnerAmt;
				if (row.match_status === "Matched") {
					acc.matched += 1;
				} else if (row.match_status === "Shopify Only") {
					acc.shopifyOnly += 1;
					acc.mismatched += 1;
				} else if (row.match_status === "Partner Only") {
					acc.partnerOnly += 1;
					acc.mismatched += 1;
				} else {
					acc.mismatched += 1;
				}
			}
		}

		return acc;
	}, [filteredRows]);

	const handleMatchFilterClick = useCallback((filter) => {
		setMatchFilter((prev) => (prev === filter ? "all" : filter));
	}, []);

	const handleGatewayFilterClick = useCallback((gatewayKey) => {
		setGatewayFilter((prev) => {
			if (prev.includes(gatewayKey)) return prev.filter((key) => key !== gatewayKey);
			return [...prev, gatewayKey];
		});
		setMatchFilter("all");
	}, []);

	const clearGatewayFilter = useCallback(() => setGatewayFilter([]), []);

	const setMatchFilterAll = useCallback(() => setMatchFilter("all"), []);
	const handleMatchedFilterClick = useCallback(() => handleMatchFilterClick("matched"), [handleMatchFilterClick]);
	const handleMismatchedFilterClick = useCallback(
		() => handleMatchFilterClick("mismatched"),
		[handleMatchFilterClick],
	);
	const handleShopifyOnlyFilterClick = useCallback(
		() => handleMatchFilterClick("shopify_only"),
		[handleMatchFilterClick],
	);
	const handlePartnerOnlyFilterClick = useCallback(
		() => handleMatchFilterClick("partner_only"),
		[handleMatchFilterClick],
	);

	const gatewayCardClickHandlers = useMemo(() => {
		const handlers = {};
		for (const g of GATEWAYS) {
			handlers[g.key] = () => handleGatewayFilterClick(g.key);
		}
		return handlers;
	}, [handleGatewayFilterClick]);

	const gatewayFilterLabel = useMemo(() => {
		if (!gatewayFilter.length) return "";
		return gatewayFilter.map((key) => GATEWAY_LABEL_BY_KEY[key] || key).join(", ");
	}, [gatewayFilter]);

	const handleExportCsv = () => {
		if (!canDownload) {
			setSnackBarOpen(true);
			setSnackBar({
				message: "You do not have permission to download this data.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		if (!sortedRows.length) {
			setSnackBarOpen(true);
			setSnackBar({
				message: "No table data available to download.",
				severity: "warning",
				anchorOrigin: { horizontal: "center", vertical: "top" },
			});
			return;
		}

		const from = fromDate?.isValid() ? fromDate.format("YYYY-MM-DD") : "from";
		const to = toDate?.isValid() ? toDate.format("YYYY-MM-DD") : "to";
		downloadTableCsv(sortedRows, `refund_tracker_${from}_${to}.csv`);
	};

	const giftCardCount = tableSummary.giftCardCount;
	const trackableTotal = Math.max(tableSummary.total - giftCardCount, 0);
	const matchRate = trackableTotal ? ((tableSummary.matched / trackableTotal) * 100).toFixed(1) : "0.0";
	const isGiftCardOnlyView = tableSummary.total > 0 && giftCardCount === tableSummary.total;
	const amountDiff = tableSummary.diffShopifyAmount - tableSummary.diffPartnerAmount;
	const amountDiffSub = isGiftCardOnlyView
		? "Not applicable for gift cards"
		: amountDiff === 0
			? "Balanced"
			: amountDiff > 0
				? "Shopify higher"
				: "Partner higher";
	const gatewaySplitTotal = summary.gatewayStats
		? Object.values(summary.gatewayStats).reduce((sum, stats) => sum + (stats?.count || 0), 0)
		: 0;
	const periodLabel =
		fromDate?.isValid() && toDate?.isValid()
			? `${fromDate.format("DD MMM YYYY")} – ${toDate.format("DD MMM YYYY")}`
			: "Select date range";

	return (
		<Box px={2} pb={3}>
			{/* Header */}
			<Stack
				direction={{ xs: "column", md: "row" }}
				justifyContent="space-between"
				alignItems={{ md: "flex-start" }}
				gap={1.5}
				mb={2}>
				<Box>
					<Typography variant="h6" fontWeight={700} color="#1e293b">
						Refund Tracker
					</Typography>
					<Typography variant="body2" color="text.secondary">
						Period: {periodLabel} · {filteredRows.length} rows showing
					</Typography>
				</Box>
				<LocalizationProvider dateAdapter={AdapterDayjs}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
						<Box sx={filterItemSx}>
							<DatePicker
								label="From"
								format="DD MMM YYYY"
								value={fromDate}
								maxDate={dayjs()}
								onChange={(value) => {
									const nextFrom = value || null;
									setFromDate(nextFrom);
									setToDate((prevTo) =>
										prevTo?.isValid() && nextFrom?.isValid() && prevTo.isBefore(nextFrom, "day")
											? null
											: prevTo,
									);
									setRows([]);
									setRangeError("");
									setMatchFilter("all");
									setGatewayFilter([]);
								}}
								slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx } }}
							/>
						</Box>
						<Box sx={filterItemSx}>
							<DatePicker
								label="To"
								format="DD MMM YYYY"
								value={toDate}
								minDate={fromDate?.isValid() ? fromDate : undefined}
								maxDate={dayjs()}
								disabled={!fromDate?.isValid()}
								onChange={(value) => {
									setToDate(value || null);
									setMatchFilter("all");
									setGatewayFilter([]);
								}}
								slotProps={{ textField: { size: "small", fullWidth: true, sx: datePickerSx } }}
							/>
						</Box>
						<Button
							variant="outlined"
							size="small"
							startIcon={<FileDownloadOutlinedIcon />}
							onClick={handleExportCsv}
							disabled={!canDownload || !sortedRows.length || !!rangeError}
							sx={{ height: 40, px: 2, textTransform: "none", fontWeight: 600 }}>
							Export CSV
						</Button>
					</Stack>
				</LocalizationProvider>
			</Stack>
			{rangeError && (
				<Typography variant="caption" color="error" sx={{ mb: 1, display: "block" }}>
					{rangeError}
				</Typography>
			)}

			{/* KPI strip */}
			<Paper
				elevation={0}
				sx={{
					mb: 2,
					p: 2,
					borderRadius: 2,
					border: "1px solid #e5e7eb",
					display: "flex",
					flexWrap: "wrap",
					gap: 3,
					rowGap: 2,
				}}>
				<InfoStat label="Total Refunds" value={tableSummary.total} sub="From table data" />
				<InfoStat
					label="Match Rate"
					value={`${matchRate}%`}
					sub={`${tableSummary.matched} matched`}
					color="#047857"
				/>
				<InfoStat
					label="Shopify Amount"
					value={formatCurrency(tableSummary.shopifyAmount)}
					sub="From Shopify"
					color="#1e40af"
				/>
				<InfoStat
					label="Partner Amount"
					value={formatCurrency(tableSummary.partnerAmount)}
					sub="From partners"
					color="#7c3aed"
				/>
				<InfoStat
					label="Amount Diff"
					value={isGiftCardOnlyView ? "—" : formatCurrency(amountDiff)}
					sub={amountDiffSub}
					color={isGiftCardOnlyView || amountDiff === 0 ? "#047857" : "#b45309"}
				/>
				<InfoStat
					label="Gaps"
					value={tableSummary.shopifyOnly + tableSummary.partnerOnly}
					sub={`${tableSummary.shopifyOnly} Shopify only · ${tableSummary.partnerOnly} Partner only`}
					color="#b45309"
				/>
			</Paper>

			{/* Detail panels */}
			<Paper elevation={0} sx={{ p: 2, mb: 2, borderRadius: 2, border: "1px solid #e5e7eb" }}>
				<Stack direction="row" justifyContent="space-between" alignItems="center" mb={1.5} flexWrap="wrap" useFlexGap spacing={1}>
					<Box>
						<Typography variant="subtitle2" fontWeight={700} color="#1e293b">
							Gateway Split
						</Typography>
						<Typography variant="caption" color="text.secondary">
							Click cards to multi-select gateways
						</Typography>
					</Box>
					{gatewayFilter.length > 0 && (
						<Chip
							size="small"
							label={`Showing ${gatewayFilterLabel} · Clear`}
							onClick={clearGatewayFilter}
							sx={{ height: 24, fontWeight: 600, fontSize: 11, backgroundColor: "#e2e8f0", color: "#334155" }}
						/>
					)}
				</Stack>
				<Box
					sx={{
						display: "grid",
						gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", lg: "1fr 1fr 1fr 1fr" },
						gap: 1.5,
					}}>
					{MAIN_GATEWAYS.map((g) => (
						<GatewaySplitCard
							key={g.key}
							label={g.label}
							color={g.color}
							barColor={g.bar}
							bg={g.bg}
							stats={summary.gatewayStats?.[g.key]}
							total={gatewaySplitTotal}
							active={gatewayFilterSet.has(g.key)}
							hideGaps={g.key === "giftcard"}
							onClick={gatewayCardClickHandlers[g.key]}
						/>
					))}
				</Box>
				{(summary.gatewayStats?.other?.count || 0) > 0 && (
					<Typography
						variant="caption"
						color="text.secondary"
						sx={{
							mt: 1.25,
							display: "inline-block",
							cursor: "pointer",
							fontWeight: gatewayFilterSet.has("other") ? 700 : 400,
							color: gatewayFilterSet.has("other") ? "#1e293b" : "text.secondary",
							textDecoration: gatewayFilterSet.has("other") ? "underline" : "none",
						}}
						onClick={gatewayCardClickHandlers.other}>
						Other / unknown: {summary.gatewayStats.other.count} · {formatCurrency(summary.gatewayStats.other.amount)}
						{gatewayFilterSet.has("other") ? " (selected)" : ""}
					</Typography>
				)}
			</Paper>

			{/* Table */}
			<Paper
				elevation={0}
				sx={{
					borderRadius: 2,
					overflow: "hidden",
					border: "1px solid #e5e7eb",
					boxShadow: "0px 1px 3px rgba(0,0,0,0.08)",
					width: "100%",
				}}>
				<Box
					sx={{
						px: 2,
						py: 1.5,
						borderBottom: "1px solid #e5e7eb",
						backgroundColor: "#f8fafc",
						display: "flex",
						flexDirection: { xs: "column", md: "row" },
						gap: 1.5,
						alignItems: { md: "center" },
						justifyContent: "space-between",
					}}>
					<Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
						<FilterPill
							label={`All (${tableFilterCounts.total})`}
							active={matchFilter === "all"}
							onClick={setMatchFilterAll}
							activeSx={MATCH_FILTER_ALL_SX}
						/>
						<FilterPill
							label={`Matched (${tableFilterCounts.matched})`}
							active={matchFilter === "matched"}
							onClick={handleMatchedFilterClick}
							activeSx={MATCH_FILTER_MATCHED_SX}
						/>
						<FilterPill
							label={`Mismatched (${tableFilterCounts.mismatched})`}
							active={matchFilter === "mismatched"}
							onClick={handleMismatchedFilterClick}
							activeSx={MATCH_FILTER_MISMATCHED_SX}
						/>
						<FilterPill
							label={`Shopify Only (${tableFilterCounts.shopifyOnly})`}
							active={matchFilter === "shopify_only"}
							onClick={handleShopifyOnlyFilterClick}
							activeSx={MATCH_FILTER_SHOPIFY_ONLY_SX}
						/>
						<FilterPill
							label={`Partner Only (${tableFilterCounts.partnerOnly})`}
							active={matchFilter === "partner_only"}
							onClick={handlePartnerOnlyFilterClick}
							activeSx={MATCH_FILTER_PARTNER_ONLY_SX}
						/>
					</Stack>
					<TextField
						size="small"
						placeholder="Search order, partner, payment id, ARN..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						sx={{ width: { xs: "100%", md: 300 } }}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<SearchIcon fontSize="small" color="action" />
								</InputAdornment>
							),
						}}
					/>
				</Box>

				<TableContainer sx={{ maxHeight: 520, opacity: isSearchPending ? 0.7 : 1, transition: "opacity 120ms ease" }}>
					<Table stickyHeader size="small" sx={{ minWidth: 2000, tableLayout: "fixed" }}>
						<TableHead>
							<TableRow>
								<TableCell
									colSpan={PINNED_COLUMNS.length}
									sx={{
										...groupHeaderSx("#dbeafe", "#1e40af", "#93c5fd"),
										position: "sticky",
										left: 0,
										zIndex: 6,
										minWidth: PINNED_COLUMNS.reduce((sum, col) => sum + col.width, 0),
										width: PINNED_COLUMNS.reduce((sum, col) => sum + col.width, 0),
										boxShadow: "4px 0 8px -4px rgba(15, 23, 42, 0.12)",
										borderRight: "2px solid #93c5fd",
									}}>
									Order
								</TableCell>
								<TableCell colSpan={SHOPIFY_COLUMNS.length} sx={groupHeaderSx("#e0f2fe", "#0369a1", "#7dd3fc")}>
									Shopify
								</TableCell>
								<TableCell colSpan={PARTNER_COLUMNS.length} sx={groupHeaderSx("#f5f3ff", "#7c3aed", "#ddd6fe")}>
									Partner
								</TableCell>
							</TableRow>
							<TableRow>
								{PINNED_COLUMNS.map((col, index) =>
									renderSortableHeader(col, {
										...headerCellSx("pinned"),
										...pinnedCellSx(index, true),
										top: 36,
										zIndex: 5,
										textAlign: col.align || "left",
									}),
								)}
								{SHOPIFY_COLUMNS.map((col) =>
									renderSortableHeader(col, {
										...headerCellSx("shopify"),
										top: 36,
										zIndex: 4,
									}),
								)}
								{PARTNER_COLUMNS.map((col) =>
									renderSortableHeader(col, {
										...headerCellSx("partner"),
										top: 36,
										zIndex: 4,
									}),
								)}
							</TableRow>
						</TableHead>
						<TableBody>
							{paginatedRows.map((row, index) => (
								<RefundTableRow
									key={`${row.id}-${index}`}
									row={row}
									index={index}
									orderNameSpan={orderNameRowSpans[index] ?? 1}
								/>
							))}
							{!filteredRows.length && (
								<TableRow>
									<TableCell colSpan={COLUMNS.length} align="center" sx={{ py: 4 }}>
										<Typography color="text.secondary">No refund records found</Typography>
									</TableCell>
								</TableRow>
							)}
						</TableBody>
					</Table>
				</TableContainer>
				<TablePagination
					component="div"
					count={filteredRows.length}
					page={page}
					onPageChange={(_, newPage) => setPage(newPage)}
					rowsPerPage={rowsPerPage}
					onRowsPerPageChange={(event) => {
						setRowsPerPage(parseInt(event.target.value, 10));
						setPage(0);
					}}
					rowsPerPageOptions={[10, 25, 50, 100]}
					labelRowsPerPage="Rows per page"
					sx={{
						borderTop: "1px solid #dbeafe",
						backgroundColor: "#f8fafc",
						"& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows": {
							fontSize: 13,
						},
					}}
				/>
			</Paper>
		</Box>
	);
}
